/**
 * Configurazione i18n della shell ERP (Fase 2).
 *
 * Inizializza i18next + react-i18next con i cataloghi it-IT (default) ed en-US.
 * La lingua attiva viene allineata alla preferenza utente (`user.lingua`) tramite
 * setLanguageFromUser, chiamato dopo il caricamento del profilo.
 *
 * La migrazione delle stringhe dei singoli moduli e' incrementale: i componenti
 * usano useTranslation() e le chiavi vengono aggiunte ai cataloghi man mano.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import itIT from './it-IT.json';
import enUS from './en-US.json';

export const LINGUE_SUPPORTATE = ['it-IT', 'en-US'] as const;
export type LinguaSupportata = (typeof LINGUE_SUPPORTATE)[number];

export const DEFAULT_LINGUA: LinguaSupportata = 'it-IT';

const LINGUA_STORAGE_KEY = 'lingua';

/** Lingua iniziale: ultima scelta memorizzata (per la pagina di login pre-auth). */
function linguaIniziale(): LinguaSupportata {
  try {
    const saved = localStorage.getItem(LINGUA_STORAGE_KEY);
    if (saved && (LINGUE_SUPPORTATE as readonly string[]).includes(saved)) {
      return saved as LinguaSupportata;
    }
  } catch {
    /* localStorage non disponibile: usa il default. */
  }
  return DEFAULT_LINGUA;
}

void i18n.use(initReactI18next).init({
  resources: {
    'it-IT': { translation: itIT },
    'en-US': { translation: enUS },
  },
  lng: linguaIniziale(),
  fallbackLng: DEFAULT_LINGUA,
  interpolation: { escapeValue: false },
});

/** Allinea la lingua attiva alla preferenza utente (fallback al default). */
export function setLanguageFromUser(lingua: string | null | undefined): void {
  const target = (LINGUE_SUPPORTATE as readonly string[]).includes(lingua ?? '')
    ? (lingua as LinguaSupportata)
    : DEFAULT_LINGUA;
  try {
    localStorage.setItem(LINGUA_STORAGE_KEY, target);
  } catch {
    /* ignora */
  }
  if (i18n.language !== target) {
    void i18n.changeLanguage(target);
  }
}

export default i18n;
